import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const fmtPdf = (v) => 'N ' + Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function numberToWords(amount) {
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  function toWords(n) {
    if (n === 0) return '';
    if (n < 20) return ones[n];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
    if (n < 1000) return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' and ' + toWords(n % 100) : '');
    if (n < 1000000) return toWords(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + toWords(n % 1000) : '');
    if (n < 1000000000) return toWords(Math.floor(n / 1000000)) + ' Million' + (n % 1000000 ? ' ' + toWords(n % 1000000) : '');
    return toWords(Math.floor(n / 1000000000)) + ' Billion' + (n % 1000000000 ? ' ' + toWords(n % 1000000000) : '');
  }
  if (!amount && amount !== 0) return '';
  const n = Math.abs(Number(amount));
  const naira = Math.floor(n);
  const kobo = Math.round((n - naira) * 100);
  let words = naira === 0 ? 'Zero' : toWords(naira);
  words += ' Naira';
  if (kobo > 0) words += ', ' + toWords(kobo) + ' Kobo';
  return words + ' Only';
}

const loadImage = (src) =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const c = document.createElement('canvas');
      c.width = img.width;
      c.height = img.height;
      const ctx = c.getContext('2d');
      const isPng = src.toLowerCase().endsWith('.png');
      if (!isPng) { ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, c.width, c.height); }
      ctx.drawImage(img, 0, 0);
      resolve(c.toDataURL(isPng ? 'image/png' : 'image/jpeg'));
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });

/**
 * Generate and download an invoice PDF from stored invoice data.
 * @param {Object} inv - The invoice object from the API
 * @param {string|null} companyLogoUrl - Optional company logo URL
 */
export async function downloadInvoicePDF(inv, companyLogoUrl) {
  const doc = new jsPDF();
  const pw = doc.internal.pageSize.getWidth();
  const dateStr = new Date(inv.created_at).toLocaleDateString('en-NG', { year: 'numeric', month: 'long', day: 'numeric' });

  const [appsnImg, nisImg, companyImg] = await Promise.all([
    loadImage('/APPSN.jpg'),
    loadImage('/FCT_NIS_LOGO.png'),
    companyLogoUrl ? loadImage(companyLogoUrl) : Promise.resolve(null),
  ]);

  // Left side logos
  const logoH = 28;
  const logoY = 5;
  if (appsnImg) doc.addImage(appsnImg, 'JPEG', 6, logoY, logoH, logoH);
  if (nisImg) doc.addImage(nisImg, 'PNG', 36, logoY, logoH, logoH);

  // Centre text
  doc.setTextColor(11, 93, 30);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('APPSN FCT BRANCH', pw / 2, 16, { align: 'center' });
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(80, 80, 80);
  doc.text('Survey Fee Invoice', pw / 2, 24, { align: 'center' });
  doc.setFontSize(8);
  doc.text(`${inv.reference_code}  |  ${dateStr}`, pw / 2, 32, { align: 'center' });

  // Right side company logo
  if (companyImg) doc.addImage(companyImg, 'JPEG', pw - 34, logoY, logoH, logoH);

  // Green separator line
  doc.setDrawColor(11, 93, 30);
  doc.setLineWidth(1);
  doc.line(6, 36, pw - 6, 36);

  // Invoice info
  doc.setTextColor(0, 0, 0);
  let y = 44;
  const labelX = 14;
  const valueX = 65;

  const drawField = (label, value) => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text(label, labelX, y);
    doc.setFont('helvetica', 'normal');
    doc.text(value || '', valueX, y);
    y += 8;
  };

  drawField('REFERENCE CODE:', inv.reference_code);
  drawField('CLIENT NAME:', inv.client_name || '');
  drawField("SURVEYOR'S COMPANY:", inv.company_name || '');
  drawField('LOCATION:', inv.location || '');
  drawField('LAND SIZE:', inv.land_size || '');
  drawField('CATEGORY:', inv.category || '');
  drawField('General Area:', inv.general_area || '');

  if (inv.account_details && inv.account_details.trim()) {
    y += 2;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text("SURVEYOR'S COMPANY ACCOUNT DETAILS:", labelX, y);
    y += 6;
    doc.setFont('helvetica', 'normal');
    const acctLines = doc.splitTextToSize(inv.account_details, pw - 28);
    doc.text(acctLines, labelX, y);
    y += acctLines.length * 5;
  }

  y += 4;

  // Details section header
  doc.setFillColor(11, 93, 30);
  doc.rect(14, y, pw - 28, 8, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('DETAILS', 18, y + 6);
  y += 14;

  // Survey type
  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('Survey Fee:', labelX, y);
  doc.setFont('helvetica', 'normal');
  doc.text(inv.survey_type || '', valueX, y);
  y += 10;

  // Fee breakdown
  autoTable(doc, {
    startY: y,
    body: [
      ['Amount', fmtPdf(inv.amount)],
      ['VAT (7.5%)', fmtPdf(inv.vat)],
      ['Total', fmtPdf(inv.total)],
    ],
    bodyStyles: { fontSize: 10 },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: pw - 80 },
      1: { halign: 'right', cellWidth: 52, fontStyle: 'bold' },
    },
    margin: { left: 14, right: 14 },
    theme: 'grid',
    headStyles: { fillColor: [11, 93, 30] },
    didParseCell: (data) => {
      if (data.row.index === 2) {
        data.cell.styles.fillColor = [240, 253, 244];
        data.cell.styles.textColor = [11, 93, 30];
        data.cell.styles.fontSize = 11;
      }
    },
  });

  let finalY = doc.lastAutoTable.finalY + 4;

  // Total in words
  const invTotal = parseFloat(inv.total || 0);
  const totalWords = numberToWords(invTotal);
  if (totalWords) {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8);
    doc.setTextColor(11, 93, 30);
    const totalWordsLines = doc.splitTextToSize(`Total in Words: ${totalWords}`, pw - 28);
    doc.text(totalWordsLines, 14, finalY);
    finalY += totalWordsLines.length * 5 + 4;
  }

  // Payment: Mandatory Deposit Scheme — 100% of the survey fee is paid to the MDS account
  const mdsTotal = invTotal;
  doc.setFillColor(11, 93, 30);
  doc.rect(14, finalY, pw - 28, 8, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('PAYMENT DETAILS — PAY 100% TO MDS ACCOUNT', 18, finalY + 6);
  finalY += 12;

  doc.setTextColor(11, 93, 30);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text(`${fmtPdf(mdsTotal)} (100%) — Pay to MDS Account`, labelX, finalY);
  finalY += 5;
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(8);
  doc.setTextColor(30, 100, 50);
  const mdsLines = doc.splitTextToSize(`(${numberToWords(mdsTotal)})`, pw - 32);
  doc.text(mdsLines, labelX + 4, finalY);
  finalY += mdsLines.length * 5 + 8;

  // Footer
  doc.setDrawColor(11, 93, 30);
  doc.setLineWidth(0.5);
  doc.line(14, finalY, pw - 14, finalY);
  finalY += 6;
  doc.setFontSize(7);
  doc.setTextColor(120);
  doc.text('This invoice is generated based on the APPSN FCT Branch Scale of Fees for Survey Services.', 14, finalY);

  doc.save(`Invoice_${inv.reference_code}.pdf`);
}
